"use client";

import { trackEvent } from "@hopper-b2b/api";
import { HopperThemingProvider } from "@hopper-b2b/hopper-theming";
import { LandingForm as LandingFormComponent } from "@hopper-b2b/ui-core";
import { useCallback, useEffect, useMemo } from "react";
import { useInterpret } from "@xstate/react";
import {
  AuthEvents,
  AuthMachine,
  authMachine,
  AuthMachineEventType,
  useAuthState,
} from "../../machine";
import {
  AuthSource,
  AuthTrackingEvents,
  AuthType,
  ChoseSignupEvent,
  CompletedPhoneDetailsEvent,
  ViewedSignupEvent,
} from "../../types/tracking";
import { GoogleLogin } from "./components";
import { AuthStateContent } from "./AuthStateContent";
import "./styles.scss";

export type AuthFlowProps = {
  onClose: () => void;
  loginContextHeader?: string;
  googleIdentityClientId?: string;
  isGoogleSsoAuthEnabled?: boolean;
};

/**
 * Standalone auth flow for rendering outside a modal (used by /signin page).
 * WebView handling is done by the consuming page before this renders.
 */
export const AuthFlow = (props: AuthFlowProps) => {
  // useInterpret restarts the service if React remounts it (Strict Mode in dev), unlike a service started in
  // useState and stopped on cleanup, which stays stopped and drops every event.
  const service = useInterpret(authMachine, {
    devTools: import.meta.env.DEV,
  });

  return (
    <AuthMachine.Provider value={service as any}>
      <HopperThemingProvider enableHDSTheme>
        <AuthStateContent {...props} />
      </HopperThemingProvider>
    </AuthMachine.Provider>
  );
};

export const LandingForm = ({
  loginContextHeader,
  googleIdentityClientId,
  isGoogleSsoAuthEnabled,
}: Pick<
  AuthFlowProps,
  "googleIdentityClientId" | "isGoogleSsoAuthEnabled" | "loginContextHeader"
>) => {
  const [, send] = useAuthState<AuthEvents, unknown>();
  const { pathname } = window.location;

  const onContinue = useCallback(
    ({ phoneNumber }) => {
      send({
        type: AuthMachineEventType.SET_PHONE_NUMBER,
        phoneNumber: `${phoneNumber}`,
      });
      trackEvent({
        eventName: AuthTrackingEvents.CompletedPhoneDetails,
        properties: { auth_type: AuthType.Phone },
      } as CompletedPhoneDetailsEvent);

      send(AuthMachineEventType.SEND_CODE);

      trackEvent({
        eventName: AuthTrackingEvents.ChoseSignup,
        properties: { auth_type: AuthType.Phone },
      } as ChoseSignupEvent);
    },
    [send]
  );

  useEffect(() => {
    let source;
    if (pathname.includes("flights")) {
      source = AuthSource.Flights;
    } else if (pathname.includes("hotels")) {
      source = AuthSource.Hotels;
    }

    trackEvent({
      eventName: AuthTrackingEvents.ViewedSignup,
      ...(source && { properties: { source } }),
    } as ViewedSignupEvent);
  }, [pathname]);

  const showGoogleSso = useMemo(
    () => isGoogleSsoAuthEnabled && googleIdentityClientId,
    [isGoogleSsoAuthEnabled, googleIdentityClientId]
  );

  return (
    <LandingFormComponent
      loginContextHeader={loginContextHeader}
      onPhoneClick={onContinue}
      GoogleButton={
        showGoogleSso && (
          <GoogleLogin googleIdentityClientId={googleIdentityClientId} />
        )
      }
    />
  );
};
